import React, { useCallback, useState, useContext, useEffect } from 'react';
import { Field, CheckboxField, Label } from '@atlaskit/form';
import { Checkbox } from '@atlaskit/checkbox';
import TextField from '@atlaskit/textfield';

// eslint-disable-next-line max-len
import { LOW_TRAFFIC_THRESHOLD_RANGE } from '@/components/composed/new-slo-views/expanded/low-traffic-helper/constants';
import { flexStart, spaceGapCompact, spaceTopCozy } from '@/styles';
import { DAC_FAQS_DOC_LINK } from '@/constants';
import { ConditionalErrorMessage } from '@/components/composed/forms/form-errors';
import { ExternalLinkAction, FormFieldTooltipLabel, HelpTooltip } from '@/components/primitive';
import { EditContext, useLocationController } from '@/controllers';

import {
  descriptionStyles,
  expandButtonStyles,
  expandTextStyles,
  formFieldBlockHighlightStyles,
  formFieldBlockStyles,
  formFieldBlockTitleStyles,
} from '../../styles';
import { helperCopy } from '../../copy';

type AlertingOptionsProps = {
  lowTrafficThreshold: number | undefined;
  suppressLowTrafficAlerts: boolean | undefined;
  isUltraSteepBurnEnabled: boolean | undefined;
  isDisabled: boolean | undefined;
};

const validateLowTrafficThreshold = (value?: '' | number) => {
  if (value === undefined || value === '' || value === null) {
    return undefined;
  }

  const valueAsNumber = Number(value);

  if (valueAsNumber > LOW_TRAFFIC_THRESHOLD_RANGE[1]) {
    return `Number must be less than or equal to ${LOW_TRAFFIC_THRESHOLD_RANGE[1]}.`;
  }

  return undefined;
};

const AlertingOptions: React.FC<AlertingOptionsProps> = ({
  lowTrafficThreshold,
  suppressLowTrafficAlerts,
  isUltraSteepBurnEnabled,
  isDisabled,
}) => {
  const { isSLOGroupMemberPage } = useLocationController();

  const scrollRef = React.useRef<HTMLDivElement>(null);

  const { highlights } = useContext(EditContext);

  const [isExpanded, setIsExpanded] = useState(false);
  const [highlight, setHighlight] = useState(false);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const handleMouseOver = useCallback(() => {
    if (highlight) setHighlight(false);
  }, [highlight]);

  useEffect(() => {
    if (highlights?.lowTrafficThreshold) {
      setIsExpanded(true);
      setHighlight(true);
      // Allow time for the component to render and calculate the actual offset
      // before scrolling
      setTimeout(() => {
        if (scrollRef?.current)
          scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
      }, 300);
    }
    // Intentionally not including highlights in the dependency, this means you
    // should get your highlights state ready before this component is mounted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      css={[spaceTopCozy, formFieldBlockStyles, highlight && formFieldBlockHighlightStyles]}
      ref={scrollRef}
      onMouseOver={handleMouseOver}
    >
      <div css={[flexStart, spaceGapCompact]} onClick={handleToggleExpand}>
        <div css={[expandButtonStyles]}>
          <span>{isExpanded ? '-' : '+'}</span>
        </div>
        <span css={expandTextStyles}>alerting options</span>
      </div>
      {isExpanded && (
        <>
          {!isSLOGroupMemberPage && (
            <div css={[spaceTopCozy]}>
              <div css={[formFieldBlockTitleStyles]}>
                <Label htmlFor="">Burn Alert Options</Label>
              </div>
              <CheckboxField
                name="isUltraSteepBurnEnabled"
                defaultIsChecked={!!isUltraSteepBurnEnabled}
              >
                {({ fieldProps, error }) => (
                  <>
                    <span css={[flexStart, spaceGapCompact]}>
                      <Checkbox
                        {...fieldProps}
                        label="Enable Ultra Steep (5-minute) burn alert"
                        isDisabled={isDisabled}
                      />
                      <HelpTooltip>
                        {helperCopy.slo.isUltraSteepBurnEnabled as React.ReactNode}
                      </HelpTooltip>
                    </span>
                    <ConditionalErrorMessage error={error} />
                  </>
                )}
              </CheckboxField>
            </div>
          )}
          <div css={[spaceTopCozy]}>
            <div css={[formFieldBlockTitleStyles]}>
              <Label htmlFor="">Low Traffic Alert Options</Label>
            </div>
            <div css={[spaceTopCozy, descriptionStyles]}>
              {/* eslint-disable-next-line max-len */}
              If you are creating a new SLO, this should be left blank, that case we use a{' '}
              <ExternalLinkAction
                href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#what-is-the-default-low-traffic-threshold-`}
                text="default"
                hideIcon
              />{' '}
              threshold to mark low traffic state in order to gate events calculation parameters
              with low traffic. Once you have an idea of what the traffic looks like, you can come
              back and set it.{' '}
              <ExternalLinkAction
                href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#when-should-i-consider-setting-a-low-traffic-threshold-for-my-slo-`}
                text="Check more"
                hideIcon
              />{' '}
              on our doc.
            </div>
            <div css={spaceTopCozy}>
              <CheckboxField
                name="suppressLowTrafficAlerts"
                defaultIsChecked={!!suppressLowTrafficAlerts}
              >
                {({ fieldProps, error }) => (
                  <>
                    <span css={[flexStart, spaceGapCompact]}>
                      <Checkbox
                        {...fieldProps}
                        label={<>Suppress low traffic alerts</>}
                        isDisabled={isDisabled}
                      />
                      <HelpTooltip>
                        {helperCopy.slo.suppressLowTrafficAlerts as React.ReactNode}
                      </HelpTooltip>
                    </span>
                    <ConditionalErrorMessage error={error} />
                  </>
                )}
              </CheckboxField>
            </div>
            <div css={spaceGapCompact}>
              <Field
                name="lowTrafficThreshold"
                label={
                  <FormFieldTooltipLabel
                    label="Low Traffic Threshold"
                    content={helperCopy.slo.lowTrafficThreshold}
                  />
                }
                validate={validateLowTrafficThreshold}
                isDisabled={isDisabled}
                defaultValue={lowTrafficThreshold}
              >
                {({ fieldProps, error }) => {
                  const { onChange, ...rest } = fieldProps;
                  return (
                    <>
                      <TextField
                        maxLength={LOW_TRAFFIC_THRESHOLD_RANGE[1].toString().length}
                        placeholder="e.g. 25000"
                        type="text"
                        autoComplete="off"
                        onChange={(e: any) => {
                          e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
                          onChange(e);
                        }}
                        {...rest}
                      />
                      <ConditionalErrorMessage error={error} />
                    </>
                  );
                }}
              </Field>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AlertingOptions;
